/**
 * OAuthProvidersAdminPage — Platform Super Admin one-stop shop to register the
 * Konnect "Developer App" on each ad-platform provider (Meta, Google Ads,
 * LinkedIn Ads, TikTok Ads).
 *
 * Mental model:
 *   - The keys you paste here are the Konnect platform's credentials with the
 *     provider — they identify YOU as the developer.
 *   - They are NOT subscriber credentials. Subscribers later click "Connect"
 *     on their own Ad Accounts page and authorize their personal account
 *     against your registered Konnect app.
 *   - Pasting these keys here turns on the "Connect" button for every
 *     subscriber on the platform.
 *
 * Storage: encrypted via Fernet, written to `db.provider_app_credentials`.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save, Eye, EyeOff, Trash2, CheckCircle2, AlertCircle, ExternalLink, KeyRound, Activity, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { IntegrationKeyGuide } from '../components/shared/IntegrationKeyGuide';

const API = process.env.REACT_APP_BACKEND_URL;

// Map our internal provider IDs to the corresponding Integration Help Center guides
const GUIDE_MAP = {
  google_ads: 'google_ads',
  meta: 'meta',
  linkedin_ads: 'linkedin',
  tiktok_ads: 'tiktok_ads',
};

const PROVIDER_VISUAL = {
  google_ads: { bg: 'from-blue-50 to-yellow-50', accent: 'border-yellow-400', emoji: '🟦' },
  meta: { bg: 'from-blue-50 to-indigo-50', accent: 'border-blue-500', emoji: '🟦' },
  linkedin_ads: { bg: 'from-sky-50 to-blue-50', accent: 'border-sky-500', emoji: '🟦' },
  tiktok_ads: { bg: 'from-slate-50 to-pink-50', accent: 'border-pink-500', emoji: '🎵' },
};

export default function OAuthProvidersAdminPage() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/oauth/admin/providers`);
      setProviders(data.providers || []);
    } catch (err) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const runSelfHeal = async () => {
    setHealing(true);
    try {
      const { data } = await axios.post(`${API}/api/oauth/self-heal/run`);
      const summary = `Healer swept ${data.total} connection${data.total !== 1 ? 's' : ''}: ` +
        `${data.healed} recovered · ${data.refreshed} token${data.refreshed !== 1 ? 's' : ''} refreshed · ${data.still_bad} still bad` +
        (data.alerted ? ` · ${data.alerted} email alert${data.alerted !== 1 ? 's' : ''} sent` : '');
      if (data.still_bad === 0) toast.success(summary);
      else toast.warning(summary);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Self-heal failed');
    } finally {
      setHealing(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 mx-auto text-amber-500" />
        <p className="mt-3 text-sm text-gray-700">Super Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto page-shell" data-testid="oauth-admin-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] text-gray-400 uppercase tracking-wider mb-1">
            <KeyRound className="h-3 w-3" /> Platform Settings · Super Admin
          </div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">OAuth Apps for Ad Platforms</h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl leading-relaxed">
            Register Konnect as a developer with each ad platform <strong>once</strong>. After you save the keys
            below, every subscriber on Konnect can connect their own ad account in a single click —
            they never see these keys.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={runSelfHeal}
          disabled={healing}
          className="text-xs gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="run-self-heal"
        >
          {healing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
          Run self-heal now
        </Button>
      </div>

      <Card className="bg-emerald-50 border-emerald-200 mb-3">
        <CardContent className="p-3 text-[11px] text-emerald-900 leading-relaxed">
          <strong>Self-healing is ON.</strong> Konnect re-tests every subscriber&apos;s OAuth connection every 30 minutes,
          refreshes tokens 24h before they expire, and auto-clears stale errors on the dashboard. After
          <strong> 3 consecutive failed sweeps</strong>, every Super Admin gets a one-time email alert with the
          affected subscriber + provider. Click &ldquo;Run self-heal now&rdquo; right after deploying a fix to
          clear any red badges immediately.
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200 mb-6">
        <CardContent className="p-4 text-xs text-blue-900 leading-relaxed">
          <strong className="block mb-1">How this works</strong>
          The Client ID + Secret you paste here identify <em>Konnect-the-platform</em> with the provider.
          Subscribers don&apos;t need their own app — they just click &ldquo;Connect with Google / Meta / LinkedIn /
          TikTok&rdquo; on their Ad Accounts page and authorize their personal ad account against your registered
          Konnect app. Tokens belong to each subscriber and are stored encrypted, scoped to their tenant.
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="space-y-4">
          {providers.map(p => (
            <ProviderCard key={p.provider} provider={p} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ provider, onSaved }) {
  const visual = PROVIDER_VISUAL[provider.provider] || { bg: 'from-gray-50 to-white', accent: 'border-gray-300' };
  const guideKey = GUIDE_MAP[provider.provider];
  const [open, setOpen] = useState(!provider.configured);
  const [fields, setFields] = useState({});
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...fields, enabled: true };
      await axios.put(`${API}/api/oauth/admin/providers/${provider.provider}`, payload);
      toast.success(`${provider.name} app saved`);
      setFields({});
      onSaved();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/api/oauth/admin/providers/${provider.provider}/test`);
      if (data.ok) {
        toast.success(data.message);
        if (data.warning) toast.info(data.warning, { duration: 6000 });
      } else {
        toast.error(data.message);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleAudit = async (forceRefresh = false) => {
    setAuditing(true);
    if (!forceRefresh) setAuditResult(null);
    try {
      const url = `${API}/api/oauth/admin/providers/${provider.provider}/audit${forceRefresh ? '?force=true' : ''}`;
      const { data } = await axios.post(url);
      setAuditResult(data);
      const issues = data.expired + data.errored;
      if (data.from_cache && !forceRefresh) {
        toast.info(`Audit (cached) · ${data.healthy}/${data.total} healthy · Click "Force refresh" for live data`);
      } else if (issues === 0 && data.total > 0) {
        toast.success(`All ${data.total} subscriber connection${data.total !== 1 ? 's' : ''} healthy`);
      } else if (data.total === 0) {
        toast.info('No subscribers have connected this provider yet');
      } else {
        toast.warning(`${issues} of ${data.total} connections need attention`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove platform-level credentials for ${provider.name}? Subscribers will no longer be able to connect this provider until you add them back.`)) return;
    try {
      await axios.delete(`${API}/api/oauth/admin/providers/${provider.provider}`);
      toast.success('Removed');
      onSaved();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <Card className={`bg-gradient-to-br ${visual.bg} border-l-4 ${visual.accent}`} data-testid={`provider-card-${provider.provider}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-heading font-bold flex items-center gap-2">
              {provider.name}
              {provider.configured ? (
                <Badge className="bg-emerald-100 text-emerald-800 text-[10px]" data-testid={`status-${provider.provider}`}>
                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Configured
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`status-${provider.provider}`}>
                  <AlertCircle className="h-2.5 w-2.5 mr-0.5" /> Not configured
                </Badge>
              )}
            </CardTitle>
            {provider.updated_at && (
              <p className="text-[10px] text-gray-500 mt-1">
                Last updated {new Date(provider.updated_at).toLocaleString()} by {provider.updated_by || 'admin'}
              </p>
            )}
            {provider.configured && provider.last_test_at && (
              <p className={`text-[10px] mt-1 flex items-center gap-1 ${provider.last_test_ok ? 'text-emerald-700' : 'text-red-600'}`}>
                {provider.last_test_ok
                  ? <CheckCircle2 className="h-2.5 w-2.5" />
                  : <AlertCircle className="h-2.5 w-2.5" />}
                Last test {new Date(provider.last_test_at).toLocaleString()} —
                <span className="font-medium">{provider.last_test_message || (provider.last_test_ok ? 'OK' : 'Failed')}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {guideKey && <IntegrationKeyGuide provider={guideKey} variant="inline" label="How to get keys" />}
            {provider.configured && (
              <Button
                variant="outline" size="sm"
                onClick={handleTest}
                disabled={testing}
                className="text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid={`test-${provider.provider}`}
              >
                {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
                Test
              </Button>
            )}
            {provider.configured && (
              <Button
                variant="outline" size="sm"
                onClick={() => handleAudit(false)}
                disabled={auditing}
                className="text-xs gap-1.5 border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid={`audit-${provider.provider}`}
                title="Ping /me for every subscriber connection (cached 1hr, force-refresh available)"
              >
                {auditing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3" />}
                Audit subs
              </Button>
            )}
            {provider.configured && (
              <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs" data-testid={`delete-${provider.provider}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setOpen(!open)} className="text-xs" data-testid={`toggle-${provider.provider}`}>
              {open ? 'Cancel' : (provider.configured ? 'Update' : 'Add keys')}
            </Button>
          </div>
        </div>
      </CardHeader>
      {auditResult && (
        <div className="px-4 pb-3" data-testid={`audit-result-${provider.provider}`}>
          <div className="bg-white border border-purple-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-purple-900 uppercase tracking-wider flex items-center gap-2">
                Subscriber connection audit
                {auditResult.from_cache && (
                  <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 normal-case tracking-normal font-normal" title={`Cached at ${auditResult.cached_at}`}>
                    Cached · {auditResult.cached_at ? new Date(auditResult.cached_at).toLocaleTimeString() : ''}
                  </Badge>
                )}
              </span>
              <div className="flex items-center gap-2 text-[10px]">
                <Badge className="bg-emerald-100 text-emerald-800">{auditResult.healthy} healthy</Badge>
                {auditResult.expired > 0 && <Badge className="bg-amber-100 text-amber-800">{auditResult.expired} expired</Badge>}
                {auditResult.errored > 0 && <Badge className="bg-red-100 text-red-800">{auditResult.errored} error</Badge>}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleAudit(true)}
                  disabled={auditing}
                  className="text-[10px] h-6 px-2 text-purple-700 hover:bg-purple-100"
                  data-testid={`audit-refresh-${provider.provider}`}
                >
                  {auditing ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : 'Force refresh'}
                </Button>
              </div>
            </div>
            {auditResult.results.length === 0 ? (
              <p className="text-[11px] text-gray-500">No subscribers have connected yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="text-gray-400 text-[10px] uppercase tracking-wider">
                    <tr><th className="text-left py-1">Tenant</th><th className="text-left py-1">Account</th><th className="text-left py-1">Status</th></tr>
                  </thead>
                  <tbody>
                    {auditResult.results.map((r, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="py-1.5 text-gray-600 truncate max-w-[140px]" title={r.tenant_id}>{r.tenant_id}</td>
                        <td className="py-1.5 text-gray-800 truncate max-w-[180px]" title={r.account_name || r.account_id}>{r.account_name || r.account_id || '—'}</td>
                        <td className="py-1.5">
                          {r.ok ? (
                            <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Healthy</span>
                          ) : (
                            <span className="text-red-600 flex items-center gap-1" title={r.message}>
                              <AlertCircle className="h-3 w-3" />
                              {r.message.length > 36 ? r.message.slice(0, 36) + '…' : r.message}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {open && (
        <CardContent className="pt-0 space-y-3">
          {provider.credential_fields.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider">
                {f.label} {f.required && <span className="text-red-500">*</span>}
                {f.is_set && (
                  <span className="ml-2 text-[10px] text-emerald-600 font-normal normal-case tracking-normal">
                    ✓ Currently set — leave blank to keep
                  </span>
                )}
              </Label>
              <div className="relative">
                <Input
                  type={f.type === 'secret' && !show[f.key] ? 'password' : 'text'}
                  value={fields[f.key] || ''}
                  onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                  placeholder={f.is_set ? '••••••••••• (currently saved — paste new value to replace)' : `Paste ${f.label} here`}
                  className="text-xs pr-10"
                  data-testid={`input-${provider.provider}-${f.key}`}
                />
                {f.type === 'secret' && (
                  <button
                    type="button"
                    onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {show[f.key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="bg-white/70 rounded-lg p-3 text-[11px] text-gray-600 leading-relaxed">
            <strong>Required Redirect URI</strong> (paste this <em>exact</em> URL into the provider&apos;s app config):
            <div className="flex items-stretch gap-1 mt-1">
              <div className="font-mono text-[10px] text-gray-800 break-all bg-gray-100 px-2 py-1.5 rounded flex-1 select-all">
                {provider.redirect_uri || `${API}/api/oauth/${provider.provider}/callback`}
              </div>
              <button
                type="button"
                onClick={() => {
                  const url = provider.redirect_uri || `${API}/api/oauth/${provider.provider}/callback`;
                  navigator.clipboard.writeText(url);
                  toast.success('Redirect URI copied');
                }}
                className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                data-testid={`copy-redirect-${provider.provider}`}
              >
                Copy
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Must include the <code className="bg-gray-100 px-1 rounded">https://</code> prefix — Google rejects path-only URIs with &ldquo;must use either http or https as the scheme&rdquo;.
            </p>
            <a
              href={
                provider.provider === 'google_ads' ? 'https://console.cloud.google.com/apis/credentials' :
                provider.provider === 'meta' ? 'https://developers.facebook.com/apps' :
                provider.provider === 'linkedin_ads' ? 'https://www.linkedin.com/developers/apps' :
                'https://business-api.tiktok.com/portal/'
              }
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2"
            >
              Open {provider.name} developer console <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={handleSave} disabled={saving} className="text-xs gap-1.5" data-testid={`save-${provider.provider}`}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save & enable for all subscribers
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
